import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Map, Marker, Popup } from 'react-leaflet';
import { LatLng } from 'leaflet';
import Screen from '../../../components/Screen';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { ISite } from '../../../@types/model/masterData/site/site';
import StandardLayerControl from '../../../components/map/StandardLayerControl';
import SiteHttpService from '../../../services/http/masterData/siteHttpService';
import { generalShowErrorSnackbar } from '../../../store/general/Functions';
import InspectionPointHttpService from '../../../services/http/masterData/inspectionPointHttpService';
import { dataSetSites, dataSetInspectionPoints } from '../../../store/masterData/Actions';
import { syncMasterData } from '../../../services/masterDataSyncService';

const SiteMap = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const sites = useAppSelector(state => state.masterData.sites);
    const selectedOrganizationIds = useAppSelector(state => state.data.selectedOrganizationIds);
    const selectedSiteIds = useAppSelector(state => state.data.selectedSiteIds);

    const [isLoading, setIsLoading] = useState(false);
    const mapRef = useRef<Map | null>(null);

    const centerPosition = useMemo(() => new LatLng(-28.4792625, 24.6727135), []);
    const mapZoom = 6;

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
                return;
            }

            setIsLoading(true);
            try {
                const [siteRes, inspectionRes] = await Promise.all([
                    SiteHttpService.getSiteData(),
                    InspectionPointHttpService.getInspectionPointData(),
                ]);

                dispatch(dataSetSites(siteRes.data));
                dispatch(dataSetInspectionPoints(inspectionRes.data));
            } catch {
                generalShowErrorSnackbar('An error occurred while loading map data.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [dispatch]);

    const setMapRef = useCallback((ref : Map | null) => {
        mapRef.current = ref;
    }, []);

    const siteMarkers = useMemo(() => {
        return sites
            .filter(x => x.isActive
                && x.latitude
                && x.longitude
                && x.organizationIds.some(y => selectedOrganizationIds?.some(z => z === y))
                && selectedSiteIds?.some(z => z === x.id))
            .map((site : ISite) => (
                <Marker
                    key={`site_id_#${site.id}`}
                    title={`${site.code} - ${site.description}`}
                    position={new LatLng(Number(site.latitude), Number(site.longitude))}
                >
                    <Popup>
                        <div className='mb3'>{`Title: ${site.code} - ${site.description}`}</div>
                        {site.shortDescription && <div className='mb3'>{`Short Description: ${site.shortDescription}`}</div>}
                        {site.telNumber && <div className='mb3'>{`Tel: ${site.telNumber}`}</div>}
                        {site.faxNumber && <div className='mb3'>{`Fax: ${site.faxNumber}`}</div>}
                        {site.packhouseDateCode && <div className='mb3'>{`Date Code: ${site.packhouseDateCode}`}</div>}
                        {site.address && (
                            <div className='fdr mb3'>
                                <div className='pr5'>Address:</div>
                                <div>{site.address}</div>
                            </div>
                        )}
                        <div className='fdr mb3'>
                            <div className='pr5'>Packhouse?:</div>
                            <div>{site.isPackhouse ? 'Yes' : 'No'}</div>
                        </div>
                        <div className='fdr mb3'>
                            <div className='pr5'>Delivery Point?:</div>
                            <div>{site.isDeliveryPoint ? 'Yes' : 'No'}</div>
                        </div>
                        <div className='fdr mb3'>
                            <div className='pr5'>BRC Compliant?:</div>
                            <div>{site.isBRCCompliant ? 'Yes' : 'No'}</div>
                        </div>
                        <div className='fdr mb3'>
                            <div className='pr5'>External?:</div>
                            <div>{site.isExternal ? 'Yes' : 'No'}</div>
                        </div>
                    </Popup>
                </Marker>
            ));
    }, [sites, selectedOrganizationIds, selectedSiteIds]);

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <div className='bcg1 PaperBorder p10 hfill wfill asc fdc aic jcc oln posr'>
                <Map
                    center={centerPosition}
                    zoom={mapZoom}
                    maxZoom={18}
                    ref={setMapRef}
                    className='flx1 bcw'
                    preferCanvas
                >
                    <StandardLayerControl position='topright' />
                    {siteMarkers}
                </Map>
            </div>
        </Screen>
    );
};

export default SiteMap;
